/**
 * Layout sidebar — top-level nested navigation section.
 *
 * Phase SIDEBAR-REGROUP (2026-07-09) — introduces a two-level tree so
 * the Owner sidebar only shows two expandable business-area parents
 * ("Certifications" + "Human Resources") which each contain the
 * pre-existing NAV_GROUPS. No route, no permission, no gating logic
 * changed — sections merely wrap the existing groups.
 *
 * A section renders NOTHING when every one of its child groups is
 * hidden (module gate + role gate + hrPerm gate all cascade up).
 */
import { useEffect, useState } from "react";
import { ChevronRight } from "lucide-react";
import SidebarGroup from "@/components/layout/SidebarGroup";
import { visibleItems, navGroupAllowedForModule } from "@/components/layout/navVisibility";

/** Resolve child groups for the section, in the declared order. */
function resolveGroups(section, allGroups) {
  const byKey = new Map(allGroups.map((g) => [g.key, g]));
  return (section.groupKeys || []).map((k) => byKey.get(k)).filter(Boolean);
}

/** A child group is considered visible iff its module is enabled AND
 *  at least one of its items passes the per-user visibility filter. */
function groupIsVisible(group, user, sub, canHR) {
  if (!navGroupAllowedForModule(group, user, sub)) return false;
  return visibleItems(group.items || [], user, sub, canHR).length > 0;
}

export default function SidebarSection({ section, allGroups, user, sub, pathname, canHR, onNavigate }) {
  const groups = resolveGroups(section, allGroups);
  const visibleGroups = groups.filter((g) => groupIsVisible(g, user, sub, canHR));

  // Any active child route auto-opens the section (and the child group
  // does the same for its items).
  const sectionHasActive = visibleGroups.some((g) =>
    (visibleItems(g.items || [], user, sub, canHR) || [])
      .some((it) => pathname === it.to || pathname.startsWith(it.to + "/"))
  );

  const STORAGE_KEY = `nav.section.${section.key}`;
  const persisted = typeof window !== "undefined"
    ? window.localStorage.getItem(STORAGE_KEY) : null;
  const isOwnerAdmin = (user?.is_owner)
    || (user?.roles || []).some((r) => r === "Owner" || r === "Admin");
  // Same default-open discipline SidebarGroup uses so the Owner/Admin
  // sees the sections open on first login. Users can collapse via the
  // header button and their preference persists in localStorage.
  const defaultOpen = sectionHasActive || isOwnerAdmin;
  const [open, setOpen] = useState(persisted == null ? defaultOpen : persisted === "1");

  useEffect(() => {
    if (sectionHasActive && !open) setOpen(true);
  }, [pathname]);

  if (visibleGroups.length === 0) return null;

  const toggle = () => {
    const next = !open;
    setOpen(next);
    try { window.localStorage.setItem(STORAGE_KEY, next ? "1" : "0"); }
    catch (e) { /* ignore */ }
  };

  const Icon = section.icon;

  return (
    <div className="mb-1.5" data-testid={section.testid || `nav-section-${section.key}`} data-active={sectionHasActive ? "1" : "0"} data-open={open ? "1" : "0"}>
      <button
        onClick={toggle}
        type="button"
        title={section.label}
        aria-expanded={open}
        data-testid={`nav-section-toggle-${section.key}`}
        className={`w-full flex items-center justify-between gap-2 px-3 py-2 rounded-md text-[11px] uppercase tracking-widest font-bold ${sectionHasActive ? "text-white" : "text-slate-200"} hover:text-white hover:bg-white/5`}
      >
        <span className="flex items-center gap-2 min-w-0">
          {Icon ? <Icon className="w-4 h-4 shrink-0" /> : null}
          <span className="truncate">{section.label}</span>
        </span>
        <ChevronRight className={`w-3.5 h-3.5 transition-transform shrink-0 ${open ? "rotate-90" : ""}`} />
      </button>
      {open ? (
        <div className="mt-0.5 pl-2 border-l border-white/5 ml-3" data-testid={`nav-section-body-${section.key}`}>
          {visibleGroups.map((g) => (
            <SidebarGroup
              key={g.key}
              group={g}
              user={user}
              sub={sub}
              pathname={pathname}
              canHR={canHR}
              onNavigate={onNavigate}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}
